import * as React from 'react';
import { Children, cloneElement, isValidElement, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslate, useGetIdentity } from 'ra-core';
import {Tooltip, IconButton, Menu, Button, Avatar, PopoverOrigin, Box} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { UserIdentity } from 'react-admin';

const defaultIcon = <AccountCircle />;

const AnchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'right',
};

const TransformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
};

export const LLUserMenu = (props: UserMenuProps) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const translate = useTranslate();
  const { isLoading, identity = {} as UserIdentity } = useGetIdentity();
  const sx = {
    user: {},
    userButton: {
      textTransform: 'none',
    },
    avatar: {
      width: '40px',
      height: '40px',
    },
  };
  
  const { last_name = '', first_name = '', email = '' } = identity;
  const name = `${first_name} ${last_name}`.trim() || email;
  
  const { children, label = 'ra.auth.user_menu', icon = defaultIcon, logout } = props;
  
  if (!logout && !children) return null;
  const open = Boolean(anchorEl);
  
  const handleMenu = (event: any) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  
  return (
    <Box sx={sx.user}>
      {!isLoading && identity?.fullName ? (
        <Button
          aria-label={label && translate(label, { _: label })}
          sx={sx.userButton}
          color="inherit"
          startIcon={
            identity.avatar ? (
              <Avatar
                sx={sx.avatar}
                src={identity.avatar}
                alt={identity.fullName}
              />
            ) : (
              icon
            )
          }
          onClick={handleMenu}
        >
          {name}
        </Button>
      ) : (
        <Tooltip title={label && translate(label, { _: label })}>
          <IconButton
            aria-label={label && translate(label, { _: label })}
            aria-owns={open ? 'menu-appbar' : undefined}
            aria-haspopup={true}
            color="inherit"
            onClick={handleMenu}
            size="large"
          >
            {icon}
          </IconButton>
        </Tooltip>
      )}
      <Menu
        id="menu-appbar"
        disableScrollLock
        anchorEl={anchorEl}
        anchorOrigin={AnchorOrigin}
        transformOrigin={TransformOrigin}
        // Make sure the menu is display under the button and not over the appbar
        // See https://v4.mui.com/components/menus/#customized-menus
        // getContentAnchorEl={null} todo: mui v5
        open={open}
        onClose={handleClose}
      >
        {Children.map(children, (menuItem) =>
          isValidElement(menuItem)
            ? cloneElement<any>(menuItem, {
              onClick: handleClose,
            })
            : null
        )}
        {logout}
      </Menu>
    </Box>
  );
};

LLUserMenu.propTypes = {
  children: PropTypes.node,
  classes: PropTypes.object,
  label: PropTypes.string,
  logout: PropTypes.element,
  icon: PropTypes.node,
};

export interface UserMenuProps {
  children?: React.ReactNode;
  classes?: any;
  label?: string;
  logout?: React.ReactNode;
  icon?: React.ReactNode;
}
